import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Index } from '../../../models/index';
import { Country } from '../../../models/country';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Observable, switchMap } from 'rxjs';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-index-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './index-list.component.html',
  styleUrl: './index-list.component.scss'
})
export class IndexListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  private lookupService = inject(LookupDataService);

  indices = signal<Index[]>([]);
  isSearchVisible = false;
  searchFilter: LookupTableSearchFilter = {};

  countries$: Observable<Country[] | null>;
  countriesLoading$: Observable<boolean>;
  private countries: Country[] = [];

  indicesColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true, required: true },
    { key: 'rank', header: 'Rank', type: 'text', sortable: true, editable: true },
    { key: 'isInternational', header: 'Is International', type: 'boolean', sortable: true, editable: true },
    { key: 'countries', header: 'Countries', type: 'multiselect', sortable: false, editable: true, required: true, selectOptions: [] }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {
    this.countries$ = this.lookupService.getTableStream<Country>('countries');
    this.countriesLoading$ = this.lookupService.getTableLoadingState('countries');
    this.countries$.subscribe(data => {
      if (data) {
        this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
        // Update the countries column selectOptions
        const countriesColumn = this.indicesColumns.find(col => col.key === 'countries');
        if (countriesColumn) {
          countriesColumn.selectOptions = this.countries.map(c => ({ value: c, label: c.name }));
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadIndices();
  }

  loadIndices(): void {
    this.spinnerService.show();
    this.lutService.getStockIndices().subscribe({
      next: (data) => {
        const sortedData = this.sortIndices(data);

        // For testing: Add first 70 countries to the first index
        // if (sortedData.length > 0 && this.countries.length > 0) {
        //   sortedData[0].countries = this.countries.slice(0, 70);
        //   sortedData[5].countries = this.countries.slice(33, 44);
        //   sortedData[7].countries = this.countries.slice(8, 9);
        //   sortedData[10].countries = this.countries.slice(55,60);
        // }

        this.indices.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading stock indices:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load Stock Indices:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  private sortIndices(indices: Index[]): Index[] {
    return [...indices].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateIndex(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onItemSelected(index: Index): void {
  }

  onEditIndex(index: Index): void {
  }

  onDeleteIndex(index: Index): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(index.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalIndices = this.indices();

          // 1. Optimistically remove from local state immediately
          this.indices.update(indices => indices.filter(idx => idx.id !== index.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteStockIndex(index.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Stock index deleted successfully');
              // 3. Update cache so other components get updated data
              this.lookupService.updateTableData('stockIndices', this.indices());
            },
            error: (error) => {
              console.error('Error deleting stock index:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.indices.set(originalIndices);
              this.toast.error(`<strong>Failed to delete Stock Index:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: Index, newName: string} | {item: Index, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: Index;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.indices.update(indices => {
      const updated = indices.map(idx => idx.id === event.item.id ? updatedItem : idx);
      return this.sortIndices(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateStockIndex(event.item.id, updatedItem).pipe(
      switchMap((response) => {
        // Extract country IDs from the countries array
        const countryIds = updatedItem.countries?.map(c => c.id) || [];
        return this.lutService.updateCountryAccess(event.item.id, countryIds).pipe(
          switchMap(() => this.lutService.getStockIndexById(event.item.id))
        );
      })
    ).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock index updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.indices.update(indices => {
          const updated = indices.map(idx => idx.id === event.item.id ? response : idx);
          return this.sortIndices(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupService.updateTableData('stockIndices', this.indices());
      },
      error: (error) => {
        console.error('Error updating stock index:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.indices.update(indices =>
          indices.map(idx => idx.id === event.item.id ? originalItem : idx)
        );
        this.toast.error(`<strong>Failed to update Stock Index:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();

    const newIndex: Index = {
      id: tempId,
      name: event['name'],
      rank: event['rank'] || 0,
      isInternational: event['isInternational'] || false,
      countries: event['countries'] || []
    };

    // 1. Optimistically add to local state immediately and sort
    this.indices.update(indices => this.sortIndices([...indices, newIndex]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createStockIndex({
      name: event['name'],
      rank: event['rank'] || 0,
      isInternational: event['isInternational'] || false,
      countries: event['countries'] || []
    }).pipe(
      switchMap((response) => {
        // Extract country IDs from the countries array
        const countryIds = (event['countries'] || []).map((c: Country) => c.id);
        return this.lutService.updateCountryAccess(response.id, countryIds).pipe(
          switchMap(() => this.lutService.getStockIndexById(response.id))
        );
      })
    ).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock index created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.indices.update(indices => {
          const updated = indices.map(idx => idx.id === tempId ? response : idx);
          return this.sortIndices(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupService.updateTableData('stockIndices', this.indices());
      },
      error: (error) => {
        console.error('Error creating stock index:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.indices.update(indices =>
          indices.filter(idx => idx.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Stock Index:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.spinnerService.show();
    this.lutService.getStockIndices().subscribe({
      next: (data) => {
        let filteredData = data;
        if (searchFilter.name) {
          filteredData = data.filter(idx => idx.name.toLowerCase().includes(searchFilter.name?.toLowerCase() ?? ''));
        }
        const sortedData = this.sortIndices(filteredData);
        this.indices.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading stock indices:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load Stock Indices:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
